/**
 * Emporium — Analytics Engine (consent-gated)
 *
 * Collects first-party behavioural events and batches them to the backend
 * for aggregation into the admin analytics dashboard and (later) rail
 * personalization.
 *
 * Every call is gated on analytics consent (hasConsent("analytics")) — if
 * the visitor hasn't opted in, fire() is a no-op and nothing is queued. The
 * server independently re-checks consent per-event (defence in depth).
 *
 * Events are queued in memory and flushed as a batch (POST /events) when
 * 10 events accumulate, 15 seconds elapse, or the page is hidden
 * (navigator.sendBeacon on `pagehide`) — never a request per call. Failed
 * flushes are silently dropped, no retry queue.
 *
 * Events tracked:
 *   session_start      — new browser session began
 *   session_end        — session ended (beforeunload), value = duration in seconds
 *   view               — product detail page viewed
 *   add_to_cart        — product added to cart
 *   pair               — two products viewed in the same session (cross-sell signal)
 *   search             — search performed (search_query, result_count)
 *   search_zero_result — search performed with zero results
 *   filter_use         — a category/filter chip was selected
 *   view (category)    — a department/category page was viewed (category =
 *                         the All Categories taxonomy id, e.g. "phones-tablets";
 *                         feeds CategoryRanking's views fallback, Part 6)
 *   dwell_time         — time spent on a page (path, value = seconds)
 *   scroll_depth       — max scroll depth reached (path, value = percent)
 *   checkout_step      — funnel step reached (category = step name)
 *   cart_abandon       — cart left non-empty on page exit (value = total, result_count = item count)
 *   nav_category_impression — a navbar category chip was genuinely viewed (Phase 2.6)
 *   nav_category_click      — a navbar category chip was clicked (Phase 2.6)
 */

import { API_BASE as API } from "../api/api";
import { hasConsent, getVisitorId } from "./consent";

// ── Session ID (anonymous, per-tab) ───────────────────────────

export function getSessionId() {
  let sid = sessionStorage.getItem("hr_sid");
  if (!sid) {
    sid = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    sessionStorage.setItem("hr_sid", sid);
  }
  return sid;
}

const SESSION_ID    = getSessionId();
const SESSION_START = Date.now();

// Products viewed this session — used for pair detection
const SESSION_VIEWS = new Set(
  JSON.parse(sessionStorage.getItem("hr_sv") || "[]")
);

function persistSessionViews() {
  sessionStorage.setItem("hr_sv", JSON.stringify([...SESSION_VIEWS]));
}

// ── Batching (queue in memory, flush on size/time/pagehide) ────

const FLUSH_SIZE         = 10;
const FLUSH_INTERVAL_MS  = 15000;
const QUEUE              = [];
let   flushTimer         = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

function flush(useBeacon = false) {
  if (QUEUE.length === 0) return;
  const events = QUEUE.splice(0, QUEUE.length);
  const body   = JSON.stringify({ events });
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/events`, new Blob([body], { type: "application/json" }));
    } else {
      fetch(`${API}/events`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body,
      }).catch(() => {}); // silent drop on failure — no retry
    }
  } catch {}
}

// Final best-effort flush on page exit — additive to trackSessionEnd's own
// dedicated beforeunload beacon below, not a replacement for it.
window.addEventListener("pagehide", () => flush(true));

// ── Low-level fire (queues, never throws, consent-gated) ───────

// Set by AuthContext whenever the signed-in user changes. Every event fired
// while signed in carries the account, so history survives a cleared
// localStorage or a switch to another machine. Module-level rather than a
// per-call argument because ~20 call sites should not each have to remember.
let CURRENT_USER_ID = null;

export function setAnalyticsUser(userId) {
  CURRENT_USER_ID = userId || null;
}

function fire(payload) {
  if (!hasConsent("analytics")) return;
  try {
    QUEUE.push({
      session_id: SESSION_ID,
      visitor_id: getVisitorId(),
      user_id: CURRENT_USER_ID,   // explicit payload user_id still wins below
      ...payload,
    });
    if (QUEUE.length >= FLUSH_SIZE) {
      if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
      flush();
    } else {
      scheduleFlush();
    }
  } catch {}
}

// ── Public API ─────────────────────────────────────────────────

/**
 * Call once when the React app mounts.
 * Fires session_start only on the very first call per tab.
 */
export function trackSessionStart(userId = null) {
  if (sessionStorage.getItem("hr_ss")) return; // already started
  sessionStorage.setItem("hr_ss", "1");
  fire({ event_type: "session_start", user_id: userId });
}

/**
 * Fire when a product detail page is loaded.
 * Also detects co-viewed pairs within the same session.
 */
export function trackView(productId, category, userId = null, price = null) {
  if (!productId) return;
  fire({ event_type: "view", product_id: productId, category, user_id: userId, price });

  // Pair detection: fire a pair event for each product already seen this session
  SESSION_VIEWS.forEach((seenId) => {
    if (seenId !== productId) {
      fire({
        event_type:  "pair",
        product_id:  Math.min(seenId, productId),
        product_id2: Math.max(seenId, productId),
      });
    }
  });

  SESSION_VIEWS.add(productId);
  persistSessionViews();
}

/**
 * Fire when a department/category page (e.g. /category/phones-tablets) is
 * viewed. No product_id — `category` is the All Categories taxonomy id, and
 * feeds the views fallback in CategoryRanking (Part 6) for categories with
 * no purchase history yet.
 */
export function trackCategoryView(categoryId) {
  if (!categoryId) return;
  fire({ event_type: "view", category: categoryId });
}

/**
 * Fire when a product is added to the cart.
 */
export function trackAddToCart(productId, category, price, userId = null) {
  if (!productId) return;
  fire({ event_type: "add_to_cart", product_id: productId, category, value: price, price, user_id: userId });
}

/**
 * Fire when a search is performed. Zero-result searches are flagged with
 * their own event type so they surface in the admin "Privacy Insights" tab
 * as unmet demand.
 *
 * `searchRequestId` (Emporia Brain P1.2) is an opaque per-search-instance
 * token — stamped as this event's idempotency_key so a later trackSearchClick
 * for the same instance can be joined back to it without ever duplicating a
 * raw query string into click metadata.
 */
export function trackSearch(query, resultCount, searchRequestId = null) {
  if (!query) return;
  fire({
    event_type:      resultCount === 0 ? "search_zero_result" : "search",
    search_query:    query,
    result_count:    resultCount,
    path:            window.location.pathname,
    idempotency_key: searchRequestId,
  });
}

/**
 * Fire when a shopper clicks a product from a search-results grid
 * (Emporia Brain P1.2). Carries the opaque search_request_id from the
 * trackSearch() call that produced these results — never the raw query
 * itself — plus the clicked product's position, so ranking quality can be
 * measured without persisting free text alongside the click.
 */
export function trackSearchClick(searchRequestId, productId, position, resultCount) {
  if (!searchRequestId || !productId) return;
  fire({
    event_type:      "search_click",
    product_id:      productId,
    result_count:    resultCount,
    value:           position,
    idempotency_key: `${searchRequestId}:${productId}`,
  });
}

/**
 * Fire when a category/filter chip is selected.
 * `category` is the page/section (e.g. "suits"); `filterValue` is the
 * chip that was clicked (e.g. "Blazers", "New").
 */
export function trackFilterUse(category, filterValue) {
  fire({ event_type: "filter_use", category, search_query: filterValue, path: window.location.pathname });
}

/**
 * Fire when a homepage rail product card is genuinely VIEWED (Emporia Brain
 * P1.3) — not merely rendered. Only ever called from useViewability's
 * callback (see ProductCard.jsx), which enforces a minimum exposure time
 * first. `position` is the card's index within its rail.
 */
export function trackRailImpression(railKey, productId, position) {
  if (!railKey || !productId) return;
  fire({ event_type: "rail_impression", rail_key: railKey, product_id: productId, value: position });
}

/**
 * Fire when a rail product card is clicked (Emporia Brain P1.3) — the rail
 * counterpart to trackSearchClick.
 */
export function trackRailClick(railKey, productId, position) {
  if (!railKey || !productId) return;
  fire({ event_type: "rail_click", rail_key: railKey, product_id: productId, value: position });
}

/**
 * Fire when a navbar category chip is genuinely VIEWED (Emporia Brain
 * Phase 2.6) — only ever called from useViewability's callback in Nav.jsx,
 * same "rendered near the viewport" vs. "actually seen" distinction
 * trackRailImpression already enforces. `category` is the category slug
 * (e.g. "phones-tablets"); `position` is its index in the rendered order
 * (post-personalization, if any).
 */
export function trackNavImpression(category, position) {
  if (!category) return;
  fire({ event_type: "nav_category_impression", rail_key: "nav_categories", category, value: position });
}

/**
 * Fire when a navbar category chip is clicked (Emporia Brain Phase 2.6) —
 * the nav counterpart to trackRailClick.
 */
export function trackNavClick(category, position) {
  if (!category) return;
  fire({ event_type: "nav_category_click", rail_key: "nav_categories", category, value: position });
}

/**
 * Fire how long a visitor spent on a page (seconds).
 */
export function trackDwellTime(path, seconds) {
  if (!seconds || seconds < 8) return;
  fire({ event_type: "dwell_time", path, value: Math.round(seconds) });
}

/**
 * Fire the maximum scroll depth reached on a page (0-100).
 */
export function trackScrollDepth(path, pct) {
  if (!pct) return;
  fire({ event_type: "scroll_depth", path, value: Math.round(pct) });
}

/**
 * Fire when a checkout-funnel step is reached.
 * step: cart_viewed | checkout_opened | delivery_info_filled |
 *       payment_selected | order_placed
 */
export function trackCheckoutStep(step) {
  fire({ event_type: "checkout_step", category: step });
}

/**
 * Fire when a visitor leaves with a non-empty cart.
 */
export function trackCartAbandon(itemCount, totalValue) {
  fire({ event_type: "cart_abandon", result_count: itemCount, value: totalValue });
}

/**
 * Inbox events — feed personalization + ops metrics on which threads get
 * opened and whether buyers act on delivered-unconfirmed orders.
 */
/**
 * Real-visitor page speed (speed brief item 30): one event per Core Web
 * Vital per page view. category = the metric (LCP, INP, CLS), value = its
 * value (milliseconds; CLS is unitless), source = "web-vitals:<device>:<net>"
 * so phones on 3G can be told from laptops on Wi-Fi. Consent-gated like
 * every other event; sent with the next batch or the pagehide beacon.
 */
export function trackWebVital(name, value, device, connection) {
  if (typeof value !== "number" || !Number.isFinite(value)) return;
  fire({
    event_type: "web_vital",
    category: name,
    value: name === "CLS" ? Math.round(value * 1000) / 1000 : Math.round(value),
    path: window.location.pathname,
    source: `web-vitals:${device}:${connection}`,
  });
}

export function trackThreadOpen(threadId) {
  if (!threadId) return;
  fire({ event_type: "thread_open", path: `/inbox/${threadId}` });
}

export function trackConfirmDeliveryTap(orderId) {
  if (!orderId) return;
  fire({ event_type: "confirm_delivery_tap", path: `/orders/${orderId}` });
}

export function trackReportProblemTap(orderId) {
  if (!orderId) return;
  fire({ event_type: "report_problem_tap", path: `/orders/${orderId}` });
}

/**
 * Fire when the session ends.
 * Wired to window.beforeunload automatically below.
 */
export function trackSessionEnd() {
  if (!hasConsent("analytics")) return;
  const duration = (Date.now() - SESSION_START) / 1000;
  // Use sendBeacon so the request survives page unload
  const body = JSON.stringify({
    event_type: "session_end",
    session_id: SESSION_ID,
    visitor_id: getVisitorId(),
    user_id:    CURRENT_USER_ID,
    value:      Math.round(duration),
  });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        `${API}/analytics/event`,
        new Blob([body], { type: "application/json" })
      );
    } else {
      fire({ event_type: "session_end", value: Math.round(duration) });
    }
  } catch {}
}

// Auto-register session end on tab close
window.addEventListener("beforeunload", trackSessionEnd);
